import React, { useCallback, useContext, useRef, useState } from 'react';
import type { Snapshot } from 'recoil';
import { useRecoilCallback, useRecoilValue, waitForAll } from 'recoil';
import styled from 'styled-components';
import {
  GroupChangeType,
  PortfoliosContext,
  type ReorderableItem,
  ReorderableListGroup,
  UserContext,
} from 'venn-components';
import {
  analysisViewIdState,
  blockSubjectInputGroups,
  fundSubject,
  getNewSubjectInputId,
  getSubjectId,
  isReportState,
  nextSubjectInputNameState,
  portfolioSubject,
  privateFundSubject,
  privatePortfolioSubject,
  requestSubjects,
  type Subject,
  subjectInputGroupName,
  subjectInputGroups,
  subjectInputGroupSubjects,
  type SubjectInputId,
  subjectsAreEqual,
  type SubjectWithOptionalFeeAndFilters,
  useCachedLoadableValue,
  workspaceFeeLimitsState,
} from 'venn-state';
import { Button, Icon } from 'venn-ui-kit';
import {
  type AnalysisSubject,
  analyticsService,
  assertNotNil,
  getWorkspaceDefaultFee,
  MAX_SUBJECT_GROUP_SIZE,
  useHasFF,
  useModal,
} from 'venn-utils';
import InputTitle from './InputTitle';
import ManageFeesModal from './ManageFeesModal';
import { PanelHeader } from './PanelHeader';
import { SubjectGroupRowWrapper, SubjectItem } from './SubjectGroupRow';
import { Checkbox } from '@/components/ui/checkbox';
import { useMultiInputSelection } from './useMultiInputSelection';
import { Tooltip, TooltipContentWithArrow, TooltipTrigger } from '@/components/ui/tooltip';
import { EditFeesModal, type EditFeesModalProps } from './EditFeesModal';
import { EditTaxRatesModal } from './EditTaxRatesModal';

type ReorderableGroupItem = ReorderableItem & {
  value: SubjectWithOptionalFeeAndFilters;
};

const SubjectsSection = () => {
  const hasBlockAssignment = useHasFF('block_assignment_ff');
  const hasDefaultRLAdvisoryFeesFF = useHasFF('default_rl_advisory_fees_ff');
  const hasAdvisoryFeesForRlInvestmentsFF = useHasFF('advisory_fees_for_rl_investments_ff');
  const { hasPermission } = useContext(UserContext);
  const isReport = useRecoilValue(isReportState);
  const viewId = useRecoilValue(analysisViewIdState);
  const workspaceFeeLimits = useCachedLoadableValue(workspaceFeeLimitsState, undefined);
  const defaultFee = getWorkspaceDefaultFee(hasDefaultRLAdvisoryFeesFF ? workspaceFeeLimits : undefined);
  const { demoPortfolio } = useContext(PortfoliosContext);
  const groupIds = useRecoilValue(subjectInputGroups);
  const groupSubjects = useRecoilValue(waitForAll(groupIds.map((id) => subjectInputGroupSubjects(id))));
  const readonly = !hasPermission('STUDIO_EDIT');
  const [isFeesModalOpen, openFeesModal, closeFeesModal] = useModal();
  const [isTaxRatesModalOpen, openTaxRatesModal, closeTaxRatesModal] = useModal();
  const [selectedSubjectData, setSelectedSubjectData] = useState<Omit<EditFeesModalProps, 'onClose'>>();
  const containerRef = useRef<HTMLDivElement>(null);
  const allFilteredItemsPerGroup: Record<SubjectInputId, SubjectWithOptionalFeeAndFilters[]> = groupSubjects.reduce(
    (result, subjectGroup, index) => {
      return {
        ...result,
        [groupIds[index] as string]: subjectGroup
          .filter((subject) => subject.filteredSubjectIds?.length)
          .flatMap((subject) => subject.filteredSubjectIds),
      };
    },
    {},
  );

  const onAddNew = useRecoilCallback(
    ({ set, snapshot }) =>
      () => {
        const newId = getNewSubjectInputId();
        const newNameLoadable = snapshot.getLoadable(nextSubjectInputNameState).valueOrThrow();

        const [newSubjects, namedSubjectsForTracking] = demoPortfolio
          ? [
              [
                {
                  ...portfolioSubject(demoPortfolio.id),
                  ...(isReport && defaultFee !== undefined ? { feesMapping: { [demoPortfolio.id]: defaultFee } } : {}),
                },
              ],
              [{ type: 'portfolio', id: demoPortfolio.id, name: demoPortfolio.name }],
            ]
          : [[], []];

        analyticsService.inputsSubjectGroupCreated({
          name: newNameLoadable,
          subjects: namedSubjectsForTracking,
          sourcePage: isReport ? 'REPORT_LAB' : 'STUDIO',
          viewId,
        });

        set(subjectInputGroupName(newId), newNameLoadable);
        set(subjectInputGroupSubjects(newId), newSubjects);
        set(subjectInputGroups, (existingIds) => [...existingIds, newId]);
      },
    [demoPortfolio, defaultFee, isReport, viewId],
  );

  const trackItemsModified = useCallback(
    async (snapshot: Snapshot, groupId: SubjectInputId, newSubjects: Subject[]) => {
      const releaseSnapshot = snapshot.retain();
      try {
        const groupName = await snapshot.getPromise(subjectInputGroupName(groupId));
        const subjects = (await snapshot.getPromise(requestSubjects(newSubjects))).map((subject) => {
          return {
            type: subject.subjectType.toLowerCase(),
            name: subject.name,
            id: subject.id,
          };
        });
        analyticsService.inputsSubjectGroupModified({
          name: groupName,
          subjects,
          sourcePage: isReport ? 'REPORT_LAB' : 'STUDIO',
          viewId,
        });
      } finally {
        releaseSnapshot();
      }
    },
    [isReport, viewId],
  );

  const trackGroupChanged = useCallback(
    (
      snapshot: Snapshot,
      groupId: SubjectInputId,
      groupName: string,
      newSubjects: Subject[],
      action: GroupChangeType,
    ) => {
      if (action === GroupChangeType.EDIT) {
        trackItemsModified(snapshot, groupId, newSubjects);
      } else {
        analyticsService.inputsSubjectGroupCreated({
          name: groupName,
          subjects: newSubjects,
          sourcePage: isReport ? 'REPORT_LAB' : 'STUDIO',
          viewId,
        });
      }
    },
    [trackItemsModified, isReport, viewId],
  );

  const onFilterGroup = useRecoilCallback(
    ({ set, snapshot }) =>
      (groupId: SubjectInputId, portfolioId: number, filteredSubjectIds: string[]) => {
        set(subjectInputGroupSubjects(groupId), (current) => {
          const updatedSubjects = current.map((source) => {
            return source.type === 'portfolio' && source.portfolioId === portfolioId
              ? {
                  ...source,
                  filteredSubjectIds,
                }
              : source;
          });
          trackItemsModified(snapshot, groupId, updatedSubjects);
          return updatedSubjects;
        });
      },
  );

  const onReorderItems = useRecoilCallback(
    ({ set, snapshot }) =>
      // Since two atoms for two subject groups are set here, this function must be non-async to ensure the state is updated synchronously
      (sourceGroup: SubjectInputId, destGroup: SubjectInputId, sourceIndex: number, destIndex: number) => {
        const sourceGroupIndex = groupIds.indexOf(sourceGroup);
        const destGroupIndex = groupIds.indexOf(destGroup);
        const subject = groupSubjects[sourceGroupIndex]![sourceIndex]!;
        const destSubjects = groupSubjects[destGroupIndex]!;

        if (sourceGroup !== destGroup) {
          const isDuplicateSubject = destSubjects.some((destSubject) => subjectsAreEqual(subject, destSubject));
          const isOverSubjectLimit = destSubjects.length >= MAX_SUBJECT_GROUP_SIZE;
          if (isDuplicateSubject || isOverSubjectLimit) {
            // Cancel the swap
            return;
          }
        }

        if (sourceGroup !== destGroup) {
          set(subjectInputGroupSubjects(sourceGroup), (current) => {
            const updatedSubjects = current.filter((_, index) => index !== sourceIndex);
            trackItemsModified(snapshot, sourceGroup, updatedSubjects);
            return updatedSubjects;
          });
        }
        set(subjectInputGroupSubjects(destGroup), (current) => {
          const newArray =
            sourceGroup === destGroup ? current.filter((_, index) => index !== sourceIndex) : [...current];
          newArray.splice(destIndex, 0, subject);
          trackItemsModified(snapshot, destGroup, newArray);
          return newArray;
        });
      },
    [trackItemsModified, groupIds, groupSubjects],
  );

  const reorderableGroups = groupIds.map((id, index) => ({
    id,
    reorderableItems: groupSubjects[index]!.map((subject) => ({
      key: getSubjectId(subject),
      value: subject,
    })),
  }));

  const onDeleteItem = useRecoilCallback(
    ({ set, snapshot }) =>
      (groupId: SubjectInputId, toDelIdx: number) => {
        set(subjectInputGroupSubjects(groupId), (current) => {
          const updatedSubjects = current.filter((_, idx) => idx !== toDelIdx);
          trackItemsModified(snapshot, groupId, updatedSubjects);
          return updatedSubjects;
        });
      },
    [trackItemsModified],
  );

  const getSwapSubjectFeesMapping = useCallback(
    (newSubject: AnalysisSubject) =>
      isReport ? computeSwapSubjectFeesMapping(defaultFee, hasAdvisoryFeesForRlInvestmentsFF, newSubject) : undefined,
    [isReport, defaultFee, hasAdvisoryFeesForRlInvestmentsFF],
  );

  // onSwapSubject memoization helps prevent excessive rerendering of the swap modal and dropdown menu
  const onSwapSubject = useRecoilCallback(
    ({ set, snapshot }) =>
      (groupId: SubjectInputId, newAnalysisSubject: AnalysisSubject, idx: number) => {
        const feesMapping = getSwapSubjectFeesMapping(newAnalysisSubject);
        const newSubject: SubjectWithOptionalFeeAndFilters = {
          ...(newAnalysisSubject.fund
            ? fundSubject(newAnalysisSubject.fund.id)
            : newAnalysisSubject.portfolio
              ? portfolioSubject(newAnalysisSubject.portfolio.id)
              : newAnalysisSubject.privateFund
                ? privateFundSubject(newAnalysisSubject.privateFund.id)
                : privatePortfolioSubject(assertNotNil(newAnalysisSubject.privatePortfolio).id)),
          ...(feesMapping ? { feesMapping } : {}),
        };

        set(subjectInputGroupSubjects(groupId), (current) => {
          const newArr = [...current];
          newArr[idx] = newSubject;
          trackItemsModified(snapshot, groupId, newArr);
          return newArr;
        });
      },
    [trackItemsModified, getSwapSubjectFeesMapping],
  );

  const { inputsStates, onInputSelect } = useMultiInputSelection(subjectInputGroups, blockSubjectInputGroups);

  const groupWrapper: React.ComponentProps<
    typeof ReorderableListGroup<SubjectInputId, ReorderableGroupItem>
  >['GroupWrapper'] = useCallback(
    ({ group, children }) => {
      const { state, disabled, tooltip } = assertNotNil(inputsStates.find((input) => input.id === group.id));
      return hasBlockAssignment ? (
        <div className="flex items-start gap-[4px] px-[12px] py-[8px] last:mb-[24px] hover:bg-venn-grey-10">
          <div className="h-[22px] flex items-center">
            <Tooltip>
              {tooltip && <TooltipContentWithArrow side="right">{tooltip}</TooltipContentWithArrow>}
              <TooltipTrigger asChild disabled={!tooltip}>
                <Checkbox
                  id={`subject-group-${group.id}`}
                  state={state}
                  onCheckedChange={(state) => onInputSelect(group.id, state)}
                  disabled={readonly || disabled}
                />
              </TooltipTrigger>
            </Tooltip>
          </div>
          <SubjectGroupRowWrapper
            readonly={readonly}
            groupId={group.id}
            trackGroupChanged={trackGroupChanged}
            deletable={groupIds.length > 1}
            labelFor={`subject-group-${group.id}`}
            className="flex flex-col gap-[4px] [&>ul]:flex [&>ul]:flex-col [&>ul]:gap-[4px]"
          >
            {children}
          </SubjectGroupRowWrapper>
        </div>
      ) : (
        <SubjectGroupRowWrapper
          readonly={readonly}
          groupId={group.id}
          trackGroupChanged={trackGroupChanged}
          deletable={groupIds.length > 1}
        >
          {children}
        </SubjectGroupRowWrapper>
      );
    },
    [readonly, groupIds, trackGroupChanged, hasBlockAssignment, inputsStates, onInputSelect],
  );

  const itemRenderer = useCallback(
    (groupId: SubjectInputId, item: ReorderableGroupItem, idx: number) => {
      const showFilteredItems = !!allFilteredItemsPerGroup[groupId]?.length;

      return (
        <SubjectItem
          subject={item.value}
          groupId={groupId}
          onDelete={() => onDeleteItem(groupId, idx)}
          onSwap={(newAnalysisSubject: AnalysisSubject) => onSwapSubject(groupId, newAnalysisSubject, idx)}
          onEditFees={(subjectInputId: SubjectInputId, subject: SubjectWithOptionalFeeAndFilters) => {
            setSelectedSubjectData({ subjectInputId, subject, idx });
            openFeesModal();
          }}
          onEditTaxRates={(subjectInputId: SubjectInputId, subject: SubjectWithOptionalFeeAndFilters) => {
            setSelectedSubjectData({ subjectInputId, subject, idx });
            openTaxRatesModal();
          }}
          onFilter={onFilterGroup}
          readonly={readonly}
          hideAllocatorLauncher={false}
          showAdvisoryFee={isReport}
          showFilteredItems={showFilteredItems}
        />
      );
    },
    [
      allFilteredItemsPerGroup,
      onFilterGroup,
      readonly,
      isReport,
      onDeleteItem,
      onSwapSubject,
      openFeesModal,
      openTaxRatesModal,
    ],
  );

  return (
    <div ref={containerRef}>
      {hasBlockAssignment ? (
        <PanelHeader.Root>
          <PanelHeader.Text>Subjects</PanelHeader.Text>
          <PanelHeader.NewButton title="Subject Groups" onAddNew={onAddNew} numberOfInputs={groupIds.length} />
        </PanelHeader.Root>
      ) : (
        <InputTitle title="Subject Groups" onAddNew={onAddNew} numberOfInputs={groupIds.length} />
      )}
      <ReorderableListGroup
        hideDelete
        reorderableGroups={reorderableGroups}
        onReorderItems={onReorderItems}
        GroupWrapper={groupWrapper}
        itemRenderer={itemRenderer}
        forceDefaultMargin
      />
      {isReport && !hasBlockAssignment && (
        <ButtonContainer>
          <ButtonWithMargin dense onClick={openFeesModal}>
            <Icon type="usd-square" /> Manage Fees
          </ButtonWithMargin>
          {isFeesModalOpen && !hasBlockAssignment && (
            <ManageFeesModal
              subjectGroups={reorderableGroups}
              onClose={closeFeesModal}
              topOffset={-100}
              leftOffset={10}
            />
          )}
        </ButtonContainer>
      )}
      {isFeesModalOpen && hasBlockAssignment && selectedSubjectData && (
        <EditFeesModal {...selectedSubjectData} onClose={closeFeesModal} />
      )}
      {isTaxRatesModalOpen && selectedSubjectData && (
        <EditTaxRatesModal {...selectedSubjectData} onClose={closeTaxRatesModal} />
      )}
    </div>
  );
};

export const computeSwapSubjectFeesMapping = (
  defaultFee: number | undefined,
  hasAdvisoryFeesForRlInvestmentsFF: boolean,
  newSubject: AnalysisSubject,
): Record<string, number> | undefined => {
  if (
    defaultFee === undefined ||
    newSubject.private ||
    newSubject.isHistoricalPortfolio ||
    (newSubject.fund && !hasAdvisoryFeesForRlInvestmentsFF)
  ) {
    return undefined;
  }
  return { [String(newSubject.id)]: defaultFee };
};

const ButtonContainer = styled.div`
  width: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: baseline;
`;

export default SubjectsSection;

const ButtonWithMargin = styled(Button)`
  width: 100%;
  margin-left: 12px;
  margin-right: 12px !important;
`;
